import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

import { OFFER_TYPES } from "../../../../constants";
import {
  formatNumber,
  getCurrencySymbolFromCache,
  getLastOfferUpdateDatetime,
} from "../../../../utils";
import { deliveryCostAmount, hasDeliveryCost } from "../../../../utils/delivery-cost";
import { getLocalisedName } from "../../../../utils/i18n";
import {
  activeProposalTerms,
  formatOfferDateTime,
  formatQuantityRange,
  formatTotalPrice,
  formatUnitPrice,
  offerDisplayStatus,
} from "../../utils";
import { Offer } from "../OfferCard/interfaces";

export const OfferSummary = ({
  offer,
  showAggregator = true,
}: {
  offer: Offer;
  showAggregator?: boolean;
}) => {
  const { t } = useTranslation();

  if (!offer?.id) {
    return null;
  }

  const displayStatus = offerDisplayStatus(offer);

  const proposal = activeProposalTerms(offer);
  const unitCode = offer.unit_of_measure?.code;
  const currency = getCurrencySymbolFromCache() || t("offerCard.currencyUnavailable");

  const price = formatNumber(proposal ? proposal.price : offer.price);
  const total = formatTotalPrice(proposal ?? offer, currency);
  const deliveryCost = proposal ? proposal.delivery_cost : offer.delivery_cost;
  const quantity = proposal
    ? formatQuantityRange(proposal.minimum_quantity, proposal.maximum_quantity)
    : formatQuantityRange(offer.minimum_quantity, offer.maximum_quantity);

  const details: { label: string; value: React.ReactNode }[] = [
    ...(offer.offer_type === OFFER_TYPES.DIRECT_OFFER && offer.deposit
      ? [
          {
            label: "depositQuantity",
            value: `${formatNumber(offer.deposit.original_quantity ?? offer.deposit.quantity)} ${unitCode}`,
          },
          {
            label: "depositPrice",
            value: formatUnitPrice(offer.deposit.price, currency, unitCode),
          },
        ]
      : []),
    ...(hasDeliveryCost(deliveryCost)
      ? [
          {
            label: "deliveryCost",
            value: `${currency}${formatNumber(deliveryCostAmount(deliveryCost))}`,
          },
        ]
      : []),
    ...(showAggregator ? [{ label: "aggregator", value: offer.tagged_aggregator?.name }] : []),
  ];

  return (
    <Card className="gap-0 py-0">
      <CardContent className="divide-y divide-border px-0">
        <div className="flex flex-wrap items-start justify-between gap-2 px-6 py-3">
          <div className="flex flex-col gap-1">
            <div className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
              {t(
                offer.offer_type === OFFER_TYPES.MARKET_OFFER
                  ? "marketOfferView.marketOffer"
                  : "marketOfferView.directOffer"
              )}{" "}
              #{offer.id} | {formatOfferDateTime(getLastOfferUpdateDatetime(offer, proposal))}
            </div>
            <div className="font-semibold text-base text-foreground">
              {t("marketOfferView.details.detailTitleTotal", {
                total,
                quantity,
                commodity: getLocalisedName(offer.buy_commodity),
                quality: offer.quality,
                price,
                unit: unitCode,
                currency,
              })}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {!offer.negotiable && (
              <Badge variant="outline">{t("marketOfferView.details.nonNegotiable")}</Badge>
            )}
            <Badge variant={displayStatus.variant}>
              {t(displayStatus.labelKey, displayStatus.params)}
            </Badge>
          </div>
        </div>

        {details.map(({ label, value }) => (
          <div key={label} className="flex items-center justify-between gap-4 px-6 py-3">
            <span className="text-muted-foreground text-sm">
              {t(`marketOfferView.details.columns.${label}`)}
            </span>
            <span className="font-medium text-foreground text-sm">{value}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
